/**
 * CastigadosPage
 * Lista de creditos DEFAULTED. Se separo de la bandeja de Cobranza porque
 * son fait accompli — el ciclo de cobranza ya cerro, no hay accion recurrente.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, User } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { ROUTES } from '@/constants';
import { useBackofficeCredits } from '@/features/credit/hooks';
import { formatCurrency } from '@/utils/formatters';
import type { BackofficeCreditsListItem } from '@/types/backoffice-crud.types';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Castigados', path: ROUTES.COLLECTION_CASTIGADOS },
];

interface CastigadoRow {
  credit: BackofficeCreditsListItem;
  writtenOffCoupons: number;
  writtenOffAmount: number;
}

export function CastigadosPage() {
  const { credits, isLoading, refetch } = useBackofficeCredits();

  const rows: CastigadoRow[] = useMemo(() => {
    return credits
      .filter((c) => c.status?.toUpperCase() === 'DEFAULTED')
      .map((c) => ({
        credit: c,
        writtenOffCoupons: c.overdue_coupons_count ?? 0,
        writtenOffAmount: c.overdue_amount_mxn ?? 0,
      }))
      .sort((a, b) => b.writtenOffAmount - a.writtenOffAmount);
  }, [credits]);

  const totalAmount = rows.reduce((s, r) => s + r.writtenOffAmount, 0);

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Castigados" subtitle="Creditos en default" breadcrumbs={BREADCRUMBS} />
        <Skeleton height={400} variant="rectangular" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Castigados"
        subtitle={`${rows.length} ${rows.length === 1 ? 'credito' : 'creditos'} en default · ${formatCurrency(totalAmount, 'MXN', 'es-MX')}`}
        breadcrumbs={BREADCRUMBS}
        actions={
          <Button onClick={() => refetch()}>
            <RefreshCw size={16} /> Actualizar
          </Button>
        }
      />

      {rows.length === 0 ? (
        <div className="rounded-lg border border-border bg-card p-12 text-center text-sm text-muted-foreground">
          No hay creditos castigados.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="w-full text-xs">
            <thead className="bg-muted/30">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Cliente</th>
                <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Credito</th>
                <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Monto original</th>
                <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Plazo</th>
                <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Cupones castigados</th>
                <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Monto castigado</th>
                <th className="w-9" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const detailLink = `/credit/detail/${r.credit.credit_id}?directoryId=${r.credit.directory_id}&from=castigados`;
                const expedienteLink = `/backoffice/expedientes/${r.credit.directory_id}?from=castigados`;
                return (
                  <tr key={r.credit.credit_id} className="border-t border-border/30 hover:bg-accent/30">
                    <td className="px-3 py-2">
                      <Link to={detailLink} className="block">
                        <div className="font-medium text-foreground">{r.credit.full_name || '—'}</div>
                        <div className="text-[10px] text-muted-foreground">{r.credit.phone_number || '—'}</div>
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <Link to={detailLink} className="block font-mono text-[10px] text-muted-foreground">
                        {r.credit.credit_id.slice(0, 12)}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-foreground">
                      {formatCurrency(r.credit.amount_mxn, 'MXN', 'es-MX')}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {r.credit.term_weeks} sem
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="inline-block rounded bg-zinc-500/15 px-2 py-0.5 text-[11px] font-bold text-zinc-300">
                        {r.writtenOffCoupons}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-zinc-300">
                      {formatCurrency(r.writtenOffAmount, 'MXN', 'es-MX')}
                    </td>
                    <td className="px-1 py-2 text-right">
                      <Link
                        to={expedienteLink}
                        title="Ver expediente del cliente"
                        aria-label="Ver expediente del cliente"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                      >
                        <User size={14} />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
